import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, HostBinding, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

import { isTotalRow } from './grid-config/ima-grid-config-context';

interface AgVendorEstimateCellParams extends ICellRendererParams {
  dataPendoId: string;
  editMode$: BehaviorSubject<boolean>;
  editVendorEstimatePermission: boolean;
  isVendorEstimatesLocked$: BehaviorSubject<boolean>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective],
  selector: 'aux-ag-vendor-estimate-cell',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    @if (disabled) {
      <div
        class="absolute left-0 h-full w-full bg-transparent"
        auxTooltip
        [template]="
          getTooltipTemplate(permissionTemplate, noteTemplate, noReceiveVendorEstimateTemplate)
        "
      ></div>
    }

    <ng-template #noteTemplate>
      <div class="space-y-1">
        {{ tooltipText }}
      </div>
    </ng-template>

    <ng-template #permissionTemplate>
      <div class="space-y-1">
        {{ doNotHavePermissionMsg }}
      </div>
    </ng-template>

    <ng-template #noReceiveVendorEstimateTemplate>
      <div class="space-y-1">
        {{ noReceiveVendorEstimateMsg }}
      </div>
    </ng-template>

    <div>
      <span>{{ params.valueFormatted }}</span>
    </div>
  `,
})
export class AgVendorEstimateCellComponent implements ICellRendererAngularComp {
  readonly doNotHavePermissionMsg = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  readonly noReceiveVendorEstimateMsg =
    'Vendor Contract does not provide Vendor Estimate. Update in the Vendor Contract card if needed.';
  params!: AgVendorEstimateCellParams;
  readonly tooltipText = MessagesConstants.VENDOR_ESTIMATES_LOCKED;

  agInit(params: AgVendorEstimateCellParams) {
    this.params = params;
    params.eGridCell.setAttribute('data-pendo-id', params.dataPendoId);
  }

  get disabled(): boolean {
    const inEditMode = this.params.editMode$ && this.params.editMode$.value;
    const hasPermission = this.params.editVendorEstimatePermission;
    const notInTotalGroupRow = !isTotalRow(this.params);
    const vendorEstimateLocked =
      this.params.isVendorEstimatesLocked$ && this.params.isVendorEstimatesLocked$.value;
    const canEditVendorEstimate = this.params.data?.canEditVendorEstimate;

    if (!hasPermission) {
      return inEditMode;
    } else {
      return inEditMode && notInTotalGroupRow && (vendorEstimateLocked || !canEditVendorEstimate);
    }
  }

  getTooltipTemplate(
    permissionTemplate: TemplateRef<unknown>,
    noteTemplate: TemplateRef<unknown>,
    noReceiveVendorEstimateTemplate: TemplateRef<unknown>
  ): TemplateRef<unknown> | undefined {
    if (
      this.params.editMode$ &&
      this.params.editMode$.value &&
      !this.params.editVendorEstimatePermission
    ) {
      return permissionTemplate;
    } else if (this.params.isVendorEstimatesLocked$ && this.params.isVendorEstimatesLocked$.value) {
      return noteTemplate;
    } else if (!this.params.data?.canEditVendorEstimate) {
      return noReceiveVendorEstimateTemplate;
    }

    return undefined;
  }

  @HostBinding('style.opacity')
  get opacity(): number {
    return this.disabled ? 0.7 : 1;
  }

  refresh(): boolean {
    return false;
  }
}
